// typewriter — the PURE reveal math behind StreamingProse's typewriter cadence.
//
// The problem it solves: streamed `text_delta` events arrive in bursty network
// clumps, so appending each delta and re-rendering makes the visible cadence
// track arrival jitter, not a steady flow ("hackigt"). The fix is to DECOUPLE
// the visual reveal rate from delta arrival: the full streamed-so-far text lives
// in state, and a cursor advances over it at a steady chars/second rate. When
// the buffer runs far ahead of the cursor (a big burst landed), the cursor snaps
// forward so the reveal never lags badly; when the text stops growing and the
// cursor reaches the end, it settles on the full text.
//
// This module is the pure core (zero React, zero I/O, zero time source of its
// own — `elapsedMs` is passed IN): `advanceCursor` is the cadence, and
// `wordBoundaryAtOrBefore` is the REVEAL GRANULARITY that keeps the impure shell
// cheap. Revealing to a whitespace boundary (whole words, never a half-typed
// token) means the shell only re-parses markdown when a NEW word is uncovered —
// not once per animation frame — which is what makes the reveal cheap enough to
// run at 60fps. Both functions are deterministic (same inputs → same output) and
// unit-tested in typewriter.test.ts.

// The steady reveal rate, in characters per second. ~90 c/s ≈ a brisk,
// legible typewriter that reads as continuous flow rather than discrete jumps.
export const REVEAL_CHARS_PER_SECOND = 90;

// The catch-up bound: if the buffered text runs more than this many characters
// ahead of the cursor, snap the cursor forward so at most this much stays
// hidden. Keeps a bursty network from leaving the reveal minutes behind while
// still preserving a visible tail of typewriter motion.
export const MAX_LAG_CHARS = 240;

// The two tunables the cadence needs, named as one shape so callers pass a
// single typed value rather than a pair of loose numbers.
export type RevealParams = {
    readonly charsPerSecond: number;
    readonly maxLagChars: number;
};

export const defaultRevealParams: RevealParams = {
    charsPerSecond: REVEAL_CHARS_PER_SECOND,
    maxLagChars: MAX_LAG_CHARS,
};

// Advance the reveal cursor by the time elapsed since the last frame, then
// apply the catch-up floor and clamp to the available text.
//   • steady term: cursor + elapsedSeconds * charsPerSecond — the flow rate.
//   • catch-up floor: max(0, total - maxLagChars) — if the buffer ran far
//     ahead, never let more than maxLagChars stay hidden.
//   • clamp to [floor, total] — the cursor can neither exceed the text it has
//     nor (via the floor) fall implausibly far behind it.
// Pure: no clock read, no mutation — `elapsedMs` is supplied by the shell.
export const advanceCursor = (
    cursor: number,
    total: number,
    elapsedMs: number,
    params: RevealParams,
): number => {
    const stepped = cursor + (elapsedMs / 1000) * params.charsPerSecond;
    const floor = Math.max(0, total - params.maxLagChars);
    return Math.min(total, Math.max(stepped, floor));
};

const isWhitespace = (ch: string): boolean =>
    ch === " " || ch === "\n" || ch === "\t" || ch === "\r";

// Snap a raw (possibly fractional) cursor back to the end of the last WHOLE
// word at or before it — the index just after the most recent whitespace. This
// is the reveal granularity: the shell renders `text.slice(0, boundary)`, so
// the parsed output only changes when a new word is uncovered, never mid-word.
// Revealing whole whitespace-delimited tokens also keeps inline markdown tokens
// that carry no internal space (e.g. `**bold**`) from flickering as raw source.
// Once the cursor reaches the end, the full text (including any final partial
// word) is revealed so the message settles complete.
export const wordBoundaryAtOrBefore = (
    text: string,
    cursor: number,
): number => {
    const c = Math.min(Math.max(0, Math.floor(cursor)), text.length);
    if (c >= text.length) return text.length;
    for (let i = c; i > 0; i--) {
        if (isWhitespace(text[i - 1] ?? "")) return i;
    }
    return 0;
};
